<template>
  <div class="credit-usage px-2 pt-2 pb-1">
    <div class="text-overline text-medium-emphasis px-1 mb-1">{{ t('credits.title') }}</div>

    <!-- Loading -->
    <div v-if="loading && !usage" class="d-flex justify-center py-4">
      <v-progress-circular indeterminate size="24" width="2" color="primary" />
    </div>

    <!-- Error -->
    <div v-else-if="error" class="d-flex align-center px-1 py-2 text-caption text-medium-emphasis">
      <span class="flex-grow-1">{{ t('credits.loadFailed') }}</span>
      <v-btn size="x-small" variant="text" density="comfortable" @click="emit('retry')">{{ t('common.retry') }}</v-btn>
    </div>

    <template v-else-if="activePool">
      <!-- Active pool (expanded) -->
      <div class="credit-card active pa-2 mb-2">
        <div class="d-flex align-center ga-1 mb-2">
          <span class="text-body-2 font-weight-medium text-truncate">{{ activePool.poolName }}</span>
          <v-spacer />
          <v-chip size="x-small" variant="tonal" label>{{ scopeLabel(activePool.scopeType) }}</v-chip>
          <v-chip v-if="pools.length > 1" size="x-small" color="primary" variant="flat" label>{{ t('credits.active') }}</v-chip>
        </div>

        <template v-for="w in windowDefs" :key="w.key">
          <div class="mb-2">
            <template v-if="isCapped(activePool.windows[w.key])">
              <div class="d-flex align-center text-caption mb-1">
                <span>{{ w.label }}</span>
                <v-spacer />
                <span class="text-medium-emphasis">
                  {{ formatCredits(activePool.windows[w.key].spent) }} / {{ formatCredits(activePool.windows[w.key].limit) }}
                  · {{ pct(activePool.windows[w.key]) }}%
                </span>
              </div>
              <v-progress-linear
                :model-value="pct(activePool.windows[w.key])"
                :color="barColor(activePool.windows[w.key])"
                height="6"
                rounded
                bg-opacity="0.15"
              />
            </template>
            <div v-else class="text-caption text-medium-emphasis">
              {{ t('credits.usedNoLimit', { label: w.label, spent: formatCredits(activePool.windows[w.key].spent) }) }}
            </div>
          </div>
        </template>

        <div class="text-caption text-medium-emphasis mt-1">
          {{ t('credits.balanceLine', { balance: formatBalance(activePool.balance), zone: resetZone }) }}
        </div>
      </div>

      <!-- Fallback pools (only when fall-through is on) -->
      <template v-if="showFallbacks">
        <div class="text-caption text-medium-emphasis px-1 mb-1">{{ t('credits.fallback') }}</div>
        <div
          v-for="(pool, i) in otherPools"
          :key="pool.poolName + i"
          class="credit-card fallback pa-2 mb-2"
          :class="{ exhausted: isDepleted(pool) }"
        >
          <div class="d-flex align-center ga-1" @click="toggle(i)">
            <span class="text-body-2 text-truncate">{{ pool.poolName }}</span>
            <v-chip size="x-small" variant="tonal" label>{{ scopeLabel(pool.scopeType) }}</v-chip>
            <v-chip v-if="isDepleted(pool)" size="x-small" variant="text" label class="text-disabled">{{ t('credits.exhausted') }}</v-chip>
            <v-spacer />
            <span class="text-caption text-medium-emphasis">{{ formatBalance(pool.balance) }}</span>
            <v-icon size="small">{{ expanded[i] ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
          </div>

          <!-- Condensed bars (collapsed view) -->
          <div v-if="!expanded[i]" class="mt-2">
            <template v-for="w in windowDefs" :key="w.key">
              <div v-if="isCapped(pool.windows[w.key])" class="d-flex align-center ga-2 mb-1">
                <span class="text-caption text-medium-emphasis window-tag">{{ w.short }}</span>
                <v-progress-linear
                  class="flex-grow-1"
                  :model-value="pct(pool.windows[w.key])"
                  :color="barColor(pool.windows[w.key])"
                  height="4"
                  rounded
                  bg-opacity="0.15"
                />
                <span class="text-caption text-medium-emphasis">{{ pct(pool.windows[w.key]) }}%</span>
              </div>
            </template>
          </div>

          <!-- Full detail (expanded view) -->
          <div v-else class="mt-2">
            <template v-for="w in windowDefs" :key="w.key">
              <div class="mb-2">
                <template v-if="isCapped(pool.windows[w.key])">
                  <div class="d-flex align-center text-caption mb-1">
                    <span>{{ w.label }}</span>
                    <v-spacer />
                    <span class="text-medium-emphasis">
                      {{ formatCredits(pool.windows[w.key].spent) }} / {{ formatCredits(pool.windows[w.key].limit) }}
                      · {{ pct(pool.windows[w.key]) }}%
                    </span>
                  </div>
                  <v-progress-linear
                    :model-value="pct(pool.windows[w.key])"
                    :color="barColor(pool.windows[w.key])"
                    height="6"
                    rounded
                    bg-opacity="0.15"
                  />
                </template>
                <div v-else class="text-caption text-medium-emphasis">
                  {{ t('credits.usedNoLimit', { label: w.label, spent: formatCredits(pool.windows[w.key].spent) }) }}
                </div>
              </div>
            </template>
            <div class="text-caption text-medium-emphasis mt-1">
              {{ t('credits.balanceLine', { balance: formatBalance(pool.balance), zone: resetZone }) }}
            </div>
          </div>
        </div>
      </template>
    </template>

    <!-- Configured but no pools match this user (e.g. Monitor mode, or a team
         with no provisioned pool). Keep it quiet — match the muted panel style. -->
    <div v-else class="px-1 py-2 text-caption text-medium-emphasis">
      {{ t('credits.noPools') }}
    </div>
  </div>
</template>

<script setup>
import { useI18n } from '../../composables/useI18n.js'
const { t } = useI18n()
import { computed, ref } from 'vue'

const props = defineProps({
  usage: { type: Object, default: null },
  loading: { type: Boolean, default: false },
  error: { type: String, default: null },
})

const emit = defineEmits(['retry'])

const windowDefs = [
  { key: 'day', label: t('credits.daily'), short: t('credits.dayShort') },
  { key: 'week', label: t('credits.weekly'), short: t('credits.weekShort') },
  { key: 'month', label: t('credits.monthly'), short: t('credits.monthShort') },
]

const pools = computed(() => props.usage?.pools ?? [])
const activePool = computed(() => pools.value.find(p => p.isActive) || pools.value[0] || null)
const otherPools = computed(() => pools.value.filter(p => p !== activePool.value))
// Only present the other pools as "fallbacks" when spend actually spills to
// them; with fall-through off, they'd never be charged so showing them lies.
const showFallbacks = computed(() => props.usage?.fallThrough === true && otherPools.value.length > 0)
const resetZone = computed(() => props.usage?.windowTimezone || 'UTC')

// Per-fallback expand state, keyed by index.
const expanded = ref({})
function toggle(i) { expanded.value[i] = !expanded.value[i] }

function scopeLabel(scopeType) {
  if (scopeType === 'Url') return t('credits.account')
  if (scopeType === 'Project') return t('credits.scopeProject')
  if (scopeType === 'Team') return t('credits.scopeTeam')
  if (scopeType === 'Mixed') return t('credits.scopeMixed')
  return scopeType || t('credits.scopePool')
}

function isCapped(w) {
  return w && w.limit != null
}

function pct(w) {
  if (!isCapped(w) || w.limit <= 0) return 0
  return Math.round(Math.min(100, (w.spent / w.limit) * 100))
}

function barColor(w) {
  const p = pct(w)
  if (p >= 90) return 'error'
  if (p >= 75) return 'warning'
  return 'primary'
}

function isDepleted(pool) {
  if (!pool) return false
  if (pool.balance <= 0) return true
  return windowDefs.some(({ key }) => {
    const w = pool.windows?.[key]
    return isCapped(w) && w.spent >= w.limit
  })
}

function formatCredits(n) {
  const v = Number(n) || 0
  if (v >= 10) return Math.round(v).toLocaleString()
  return String(Math.round(v * 10) / 10)
}

function formatBalance(n) {
  return Math.round(Number(n) || 0).toLocaleString()
}
</script>

<style scoped>
.credit-card {
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 8px;
  background: rgba(var(--v-theme-surface-variant), 0.35);
}
.credit-card.active {
  background: rgba(var(--v-theme-surface-variant), 0.55);
}
.credit-card.fallback {
  cursor: pointer;
}
.credit-card.exhausted {
  opacity: 0.6;
}
.window-tag {
  width: 1rem;
  text-align: center;
  flex-shrink: 0;
}
</style>
